// src/router/modalRoutes.ts

// Stores
import { useModalStore } from '@/store/modalStore';
import { useUserDataStore } from '@/store/userDataStore';
import { useBottomSheetStore } from '@/store/bottomSheetStore';

// Services
import { isMobileApp } from '@/services/mobile_app/mobileAppServices';
import { sportTitle, isMobile } from '@/services/admin_actions/apiUtils';
import { getPopUpsForUser } from '@/services/admin_features/popupServices';
import { hasConsented } from '@/services/consent/cookieConsentService';

// Components
import AddToAppButton from '@/components/MobileAppComponents/AddToAppButton.vue';
import UpgradeMembership from '@/components/NotificationBanners/UpgradeMembership.vue';
import CookieConsentPanel from '@/components/ComplianceComponents/CookieConsentPanel.vue';
import ConsentPopup from '@/components/ComplianceComponents/ConsentPopup.vue';
import PopupComponent from '@/components/AdminComponents/MarketingBanners/PopupComponent.vue';

// Flag to track if modal has already opened during this session
let hasShownModalThisSession = false;

// Track last visited route path
let lastPath: string | null = null;

// The route the app is on right now, kept current by RunModalRoutes. A prompt is scheduled with a
// delay, so by the time it fires the user may have moved to a page it's excluded from - the timer
// checks this, not the route it was scheduled on.
let currentRoute: any = null;

// Timer reference for delayed modal open
let modalTimer: number | null = null;

// LocalStorage token type for dismissals
type DismissToken = { id: string; expiresAt: string };

// ----------------------------------------
// Check if a BottomSheet has been dismissed (using localStorage)
// ----------------------------------------
const isSheetDismissed = (id: string): boolean => {
  try {
    const key = `bottomsheet:${sportTitle.value.toLowerCase()}:${id}`;
    const raw = localStorage.getItem(key);
    if (!raw) return false;

    const token = JSON.parse(raw) as DismissToken;
    if (!token?.expiresAt) return false;

    return Date.now() < Date.parse(token.expiresAt);
  } catch {
    return false;
  }
};

// ----------------------------------------
// Check if a Modal has been dismissed (using localStorage)
// ----------------------------------------
const isModalDismissed = (id: string): boolean => {
  try {
    const key = `modal:${sportTitle.value.toLowerCase()}:${id}`;
    const raw = localStorage.getItem(key);
    if (!raw) return false;

    const token = JSON.parse(raw) as DismissToken;
    if (!token?.expiresAt) return false;

    return Date.now() < Date.parse(token.expiresAt);
  } catch {
    return false;
  }
};

// ----------------------------------------
// Open either a BottomSheet (mobile) or Modal (desktop)
// Only triggers if not dismissed, and sets session state
// ----------------------------------------
const openModal = (args: {
  id: string;
  title?: string;
  component: any;
  props?: Record<string, any>;
  modalOptions?: { noPadding?: boolean; whiteIcon?: boolean; persistent?: boolean };
  persistence?: {
    disablePersistence?: boolean;
    ttlDays?: number;
  };
  sheetOptions?: {
    bgColor?: 'white' | 'grey' | 'primary';
    siteLogo?: boolean;
    showOnDesktop?: boolean;
    disablePersistence?: boolean;
  };
  // Pages the prompt must not open on. Re-checked against the live route when the delay ends, so
  // navigating to one of them during the delay drops the prompt instead of opening it there.
  blockedOn?: (route: any) => boolean;
}) => {
  const modalPersistenceDisabled = args.persistence?.disablePersistence ?? true;
  const ttlDays = args.persistence?.ttlDays ?? 30;

  // Block if dismissed
  if (isMobile.value) {
    const sheetPersistenceDisabled = args.sheetOptions?.disablePersistence ?? false;
    if (!sheetPersistenceDisabled && isSheetDismissed(args.id)) return false;
  } else {
    if (!modalPersistenceDisabled && isModalDismissed(args.id)) return false;
  }

  // Flag this session as shown
  hasShownModalThisSession = true;

  // Clear existing timer if any
  if (modalTimer) {
    window.clearTimeout(modalTimer);
    modalTimer = null;
  }

  // Schedule modal/sheet to open in 3s
  modalTimer = window.setTimeout(() => {
    modalTimer = null;

    // Moved to an excluded page during the delay: drop it, and let a later navigation to an
    // allowed page schedule it again.
    if (args.blockedOn?.(currentRoute)) {
      hasShownModalThisSession = false;
      return;
    }

    if (isMobile.value) {
      const sheet = useBottomSheetStore();
      sheet.openSheet({
        id: args.id,
        title: args.title ?? '',
        component: args.component,
        props: args.props ?? {},
        options: {
          bgColor: args.sheetOptions?.bgColor ?? 'white',
          siteLogo: args.sheetOptions?.siteLogo ?? true,
          showOnDesktop: args.sheetOptions?.showOnDesktop ?? false,
          disablePersistence: args.sheetOptions?.disablePersistence ?? false,
        },
      });
      return;
    }

    const modalStore = useModalStore();
    modalStore.openModal({
      id: args.id,
      title: args.title ?? '',
      description: '',
      component: args.component,
      props: args.props ?? {},
      options: {
        noPadding: args.modalOptions?.noPadding ?? false,
        whiteIcon: args.modalOptions?.whiteIcon ?? false,
        persistent: args.modalOptions?.persistent ?? false,
        disablePersistence: modalPersistenceDisabled,
        ttlDays: ttlDays,
      },
    });
  }, 3000);

  return true;
};

// ----------------------------------------
// Check and open marketing popup (one-time per app open, called from DefaultLayout)
// ----------------------------------------
const TriggerQuizModal = async () => {
  if (hasShownModalThisSession) return;

  const userDataStore = useUserDataStore();
  if (userDataStore.triggers?.length) return;

  const modalStore = useModalStore();

  try {
    const response = await getPopUpsForUser();
    const popups = response?.data || [];

    const dismissedMap = JSON.parse(localStorage.getItem('dismissedPopups') || '{}');
    const now = new Date();

    for (const popup of popups) {
      if (popup.enabled !== 1) continue;

      const dismissedAt = dismissedMap[popup.id];
      if (dismissedAt) {
        const days = (now.getTime() - new Date(dismissedAt).getTime()) / (1000 * 60 * 60 * 24);
        if (days < 30) continue;
        delete dismissedMap[popup.id];
      }

      const current = modalStore.component?.value;
      if (current?.__name === 'QuizComponent') return;

      localStorage.setItem('dismissedPopups', JSON.stringify(dismissedMap));

      let parsedContent: any;
      try {
        parsedContent = JSON.parse(JSON.parse(popup.content));
      } catch {
        continue;
      }

      openModal({
        id: `popup-${popup.id}`,
        component: PopupComponent,
        props: { popupData: popup, popupContent: parsedContent },
        modalOptions: { noPadding: true, whiteIcon: true },
        persistence: { disablePersistence: true },
      });

      return;
    }
  } catch {}
};

// ----------------------------------------
// Pages where a prompt would get in the way. Paths may carry the optional /:lang prefix.
//  - login: the Vue routes flagged LogUserOut
//  - register: still a classic page, so matched by path
//  - membership/subscribe: flagged WebRestricted (store-policy pages); paths matched too in
//    case the flag moves
// ----------------------------------------
const routePath = (to: any): string =>
  String(to?.path ?? '')
    .replace(/^\/(en|fr|ar|es)(?=\/|$)/, '')
    .replace(/^\/+|\/+$/g, '');

const isLoginPage = (to: any): boolean =>
  to?.meta?.LogUserOut === true || ['login', 'app_login', 'menu_login'].includes(routePath(to));

const isRegisterPage = (to: any): boolean => routePath(to) === 'register';

const isMembershipPage = (to: any): boolean =>
  to?.meta?.WebRestricted === true || ['membership', 'subscribe'].includes(routePath(to));

// ----------------------------------------
// Show "Add to App" prompt (mobile web only). Not while signing up, nor on the
// membership/subscription pages. Fine on the login page.
// ----------------------------------------
const TriggerAddAppButton = async (to: any, from: any) => {
  if (hasShownModalThisSession) return;

  if (!isMobile.value) return;
  if (isMobileApp()) return;
  if (sportTitle.value.toLowerCase() !== 'squash') return;
  const blockedOn = (route: any) => isRegisterPage(route) || isMembershipPage(route);
  if (blockedOn(to)) return;
  if (to?.path === lastPath) return;

  lastPath = to?.path ?? null;

  openModal({
    id: 'add-to-app',
    title: 'Get the Mobile App',
    component: AddToAppButton,
    blockedOn,
    sheetOptions: {
      bgColor: 'white',
      siteLogo: true,
      showOnDesktop: false,
      disablePersistence: false,
    },
  });
};

// ----------------------------------------
// Show Upgrade Membership banner (logged-in basic members only). Never on login/register pages,
// nor on the membership/subscription pages - the prompt's own button leads there.
// ----------------------------------------
const TriggerUpgradeMembership = async (to: any) => {
  if (hasShownModalThisSession) return;

  if (!isMobile.value) return;
  const blockedOn = (route: any) => isLoginPage(route) || isRegisterPage(route) || isMembershipPage(route);
  if (blockedOn(to)) return;

  const userDataStore = useUserDataStore();
  // Nothing to upgrade without an account
  if (!userDataStore.isLoggedIn) return;

  const tiers = userDataStore.userInfo?.membership_tiers;

  if (!tiers) return;
  if (!tiers.is_visitor && !tiers.is_basic) return;

  openModal({
    id: 'membership-upgrade',
    title: `Enjoying ${sportTitle.value}Levels?`,
    component: UpgradeMembership,
    blockedOn,
    persistence: { disablePersistence: false, ttlDays: 30 },
    sheetOptions: {
      bgColor: 'white',
      siteLogo: true,
      showOnDesktop: false,
      disablePersistence: false,
    },
  });
};

// ----------------------------------------
// Prompt any logged-in user (any account source) who hasn't accepted the current ToS yet.
// Blocking on desktop (persistent modal, no close icon); on mobile the bottom sheet can still be
// swiped/closed, but disablePersistence keeps it from being suppressed — it reopens on next
// navigation for as long as the backend still reports consentRequired.
// ----------------------------------------
const TriggerConsentRequired = async () => {
  const userDataStore = useUserDataStore();
  if (!userDataStore.userInfo?.consentRequired) return;

  openModal({
    id: 'consent-required',
    title: 'Before you continue',
    component: ConsentPopup,
    modalOptions: { persistent: true },
    persistence: { disablePersistence: true },
    sheetOptions: {
      bgColor: 'white',
      siteLogo: true,
      showOnDesktop: false,
      disablePersistence: true,
    },
  });
};

// ----------------------------------------
// Show Cookie Cookie Consent (all users, mobile and desktop)
// ----------------------------------------
const TriggerCookieConsent = async () => {
  if (hasConsented() || isMobileApp()) return;

  openModal({
    id: 'cookie-consent',
    title: 'Cookie Preferences',
    component: CookieConsentPanel,
    persistence: { disablePersistence: true },
    sheetOptions: {
      bgColor: 'white',
      siteLogo: false,
      showOnDesktop: false,
      disablePersistence: true,
    },
  });
};

// ----------------------------------------
// Run all modal triggers sequentially (popup is excluded — handled once in DefaultLayout)
// ----------------------------------------
const RunModalRoutes = async (to: any, from: any) => {
  currentRoute = to;

  await TriggerConsentRequired();
  await TriggerCookieConsent();
  await TriggerAddAppButton(to, from);
  await TriggerUpgradeMembership(to);
};

export { RunModalRoutes, TriggerQuizModal };
